import { useContext, useState } from 'react';
import useSWR from 'swr';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Skeleton from '../../../../common/base/Skeleton';
import Text from '../../../../common/base/Text';
import Checkbox from '../../../../common/Form/Fields/Checkbox';
import ScrollArea from '../../../../common/helpers/ScrollArea';
import { Div } from '../../../../common/helpers/StyledUtils';
import Modal from '../../../../common/Modal';
import Search from '../../../../common/Search';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardContext';
import OutpostStaticIPSections from './OutpostStaticIPSections';
import { useCopyToClipboard } from '../../../../../utils/copy';
interface EnableStaticIPDialogProps {
  onClose: () => void;
}
export const eg_static_ip_addresses = [`34.138.140.223`, `34.138.161.100`, `35.231.250.193`, `35.196.71.29`, `34.138.56.192`];
const EnableStaticIPDialog: React.FC<EnableStaticIPDialogProps> = ({
  onClose
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateTeam,
    mutateTeams,
    team,
    teams,
    organization
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const [is_submitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const copyToClipboard = useCopyToClipboard();
  const {
    data: price_data,
    error: price_error,
    isLoading: is_loading
  } = useSWR(APIMethodKeys.billing.getAddonPrice('static_ip'), () => HookdeckAPI.billing.getAddonPrice('static_ip'));

  // Get all teams in this org
  const org_teams = (teams || []).filter(t => t.organization_id === organization?.id);

  // Teams that already have static IP enabled
  const already_enabled_ids = new Set(org_teams.filter(t_0 => t_0.static_ip_proxy).map(t_1 => t_1.id));
  const has_eg_teams = org_teams.some(t_2 => t_2.type !== 'outpost');
  const has_outpost_teams = org_teams.some(t_3 => t_3.type === 'outpost');
  const show_grouped = has_eg_teams && has_outpost_teams;

  // Selected team IDs (start with current team pre-selected if not already enabled)
  const [selected_ids, setSelectedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (team && !already_enabled_ids.has(team.id)) {
      initial.add(team.id);
    }
    return initial;
  });

  // Filtered teams based on search, sorted: current team first, then alphabetically
  const filtered_teams = (() => {
    const query = search.toLowerCase().trim();
    const matched = query ? org_teams.filter(t_4 => t_4.name.toLowerCase().includes(query)) : org_teams;
    return [...matched].sort((a, b) => {
      if (a.id === team?.id) return -1;
      if (b.id === team?.id) return 1;
      return a.name.localeCompare(b.name);
    });
  })();

  // Filtered selectable teams (for select all logic)
  const filtered_selectable_teams = filtered_teams.filter(t_5 => !already_enabled_ids.has(t_5.id));
  const all_filtered_selected = filtered_selectable_teams.length > 0 && filtered_selectable_teams.every(t_6 => selected_ids.has(t_6.id));
  const handleToggleTeam = (team_id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(team_id)) {
        next.delete(team_id);
      } else {
        next.add(team_id);
      }
      return next;
    });
  };
  const handleToggleAll = () => {
    setSelectedIds(prev_0 => {
      const next_0 = new Set(prev_0);
      if (all_filtered_selected) {
        for (const t_7 of filtered_selectable_teams) {
          next_0.delete(t_7.id);
        }
      } else {
        for (const t_8 of filtered_selectable_teams) {
          next_0.add(t_8.id);
        }
      }
      return next_0;
    });
  };
  const formatPrice = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase() || 'USD'
    }).format(amount);
  };
  const new_team_count = selected_ids.size;
  const total_cost = price_data?.unit_amount || 0;
  const currency_0 = price_data?.currency || 'USD';
  const handleEnable = async () => {
    if (selected_ids.size === 0) return;
    setIsSubmitting(true);
    try {
      const team_ids = Array.from(selected_ids);
      // Pass confirmed_price to validate the price hasn't changed since the user saw it
      const confirmed_price = price_data?.unit_amount;
      if (team_ids.length === 1 && team_ids[0] === team?.id) {
        // Single team (current team) — use the existing endpoint
        const updated_team = await HookdeckAPI.billing.enableAddon('static_ip', confirmed_price);
        mutateTeam(updated_team);
      } else {
        // Bulk enable
        const result = await HookdeckAPI.billing.enableAddonBulk('static_ip', team_ids, confirmed_price);
        // Update current team if it was in the list
        const current_team_update = result.updated_teams.find(t_9 => t_9.id === team?.id);
        if (current_team_update) {
          mutateTeam(current_team_update);
        }
      }
      mutateTeams?.();
      addToast('success', `Static IP delivery enabled for ${new_team_count} project${new_team_count !== 1 ? 's' : ''}.`);
      onClose();
    } catch (error: any) {
      const error_message = error?.response?.message || 'Failed to enable Static IP. Please try again.';
      addToast('error', error_message);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Modal title="Enable Static IP delivery" submit_label={price_data ? `Enable for ${formatPrice(total_cost, currency_0)}/month` : 'Enable'} cancel_label="Cancel" onSubmit={handleEnable} onCancel={onClose} onClose={onClose} is_submitting={is_submitting} submit_disabled={is_loading || !!price_error || !price_data || selected_ids.size === 0}>
      <Div>
        <Text as="p" m={0}>
          Enable to route all webhook deliveries for projects of your choosing through a dedicated
          set of IP addresses. This is useful when your destinations require IP allowlisting.
        </Text>

        <StyledCard m={{
        y: 4
      }} overflow_hidden>
          {has_eg_teams && <StyledCardSection muted p={3} flex={{
          direction: 'column',
          align: 'flex-start',
          gap: 3
        }}>
              <Div w={100} flex={{
            align: 'center',
            justify: 'space-between',
            gap: 2
          }}>
                <Text semi size="s">
                  {show_grouped ? 'Event Gateway IP addresses' : 'Dedicated Hookdeck IP addresses'}
                </Text>
                <Button ghost icon="copy" m={-1} onClick={() => copyToClipboard(eg_static_ip_addresses.join(', '))} />
              </Div>
              <Div flex={{
            align: 'flex-start',
            gap: 2,
            wrap: true
          }}>
                {eg_static_ip_addresses.map(address => <Badge mono subtle neutral key={address}>
                    {address}
                  </Badge>)}
              </Div>
            </StyledCardSection>}
          {has_outpost_teams && <OutpostStaticIPSections show_grouped={show_grouped} />}
        </StyledCard>

        <StyledCard overflow_hidden>
          <Search borderless small value={search} onChange={setSearch} placeholder={`Filter ${org_teams.length} projects...`} />
          <Divider />
          <ScrollArea max_h={{
          px: 174
        }} p={1}>
            {filtered_teams.length > 0 && <Div p={{
            x: 2,
            y: 1.5
          }}>
                <Checkbox name="select_all" checked={all_filtered_selected} onChange={() => handleToggleAll()} label="Select all" disabled={filtered_selectable_teams.length === 0} />
              </Div>}
            {(() => {
            const eg_teams = filtered_teams.filter(t_10 => t_10.type !== 'outpost');
            const outpost_teams = filtered_teams.filter(t_11 => t_11.type === 'outpost');
            const has_both = eg_teams.length > 0 && outpost_teams.length > 0;
            const renderTeam = (t_12: (typeof filtered_teams)[0]) => {
              const is_already_enabled = already_enabled_ids.has(t_12.id);
              const is_selected = is_already_enabled || selected_ids.has(t_12.id);
              return <Div key={t_12.id} p={{
                x: 2,
                y: 1.5
              }} flex={{
                align: 'center',
                justify: 'space-between'
              }}>
                    <Checkbox name={`team_${t_12.id}`} checked={is_selected} onChange={() => handleToggleTeam(t_12.id)} disabled={is_already_enabled} label={<Div flex={{
                  align: 'center',
                  gap: 2
                }}>
                          <Text disabled={is_already_enabled}>{t_12.name}</Text>
                          {t_12.id === team?.id && <Badge subtle neutral>
                              Current project
                            </Badge>}
                          {is_already_enabled && <Badge subtle neutral>
                              Enabled
                            </Badge>}
                        </Div>} />
                  </Div>;
            };
            return <>
                  {eg_teams.length > 0 && <>
                      {has_both && <Div p={{
                  x: 2,
                  y: 1
                }}>
                          <Text muted semi size="xs">
                            Event Gateway
                          </Text>
                        </Div>}
                      {eg_teams.map(renderTeam)}
                    </>}
                  {outpost_teams.length > 0 && <>
                      {has_both && <Div p={{
                  x: 2,
                  y: 1
                }}>
                          <Text muted semi size="xs">
                            Outpost
                          </Text>
                        </Div>}
                      {outpost_teams.map(renderTeam)}
                    </>}
                </>;
          })()}
            {filtered_teams.length === 0 && <Div p={{
            x: 3,
            y: 4
          }} flex={{
            justify: 'center'
          }}>
                <Text muted>No projects found</Text>
              </Div>}
          </ScrollArea>
        </StyledCard>

        {price_error && <Div flex={{
        align: 'center',
        gap: 2
      }} p={3} m={{
        t: 4
      }}>
            <Icon icon="error" danger />
            <Text danger>Unable to load pricing information. Please try again later.</Text>
          </Div>}

        {!price_error && <>
            <Div flex={{
          justify: 'space-between',
          align: 'center'
        }} m={{
          t: 4
        }}>
              <Text>Total cost</Text>
              {price_data ? <Div flex={{
            align: 'center'
          }}>
                  <Text semi>{formatPrice(total_cost, currency_0)}</Text>
                  <Text muted>/month</Text>
                </Div> : <Skeleton w={{
            px: 100
          }} h={{
            px: 24
          }} loading={is_loading} />}
            </Div>
            <Div flex={{
          align: 'flex-start',
          gap: 1
        }}>
              <Icon icon="info" muted m={{
            t: 0.5
          }} />
              <Text muted size="s">
                Fee will be prorated and added to your next invoice. Disable anytime.
              </Text>
            </Div>
          </>}
      </Div>
    </Modal>;
};
export default EnableStaticIPDialog;